import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { OffersService } from '../services/offers.service';
import { NewsService } from '../services/news.service';
import { PartnersService } from '../services/partners.service';
import { BannersService } from '../services/banners.service';
import { Offer } from '../interfaces/offers.interface';
import { Router } from '@angular/router';
import { News } from '../interfaces/news.interface';
import { Banner } from '../interfaces/banner.interface';
import { environment } from '../../environments/environment';
import { Partner, PartnerGroup } from '../interfaces/partners.interface';
import { forkJoin, Subscription } from 'rxjs';
import { UsersService } from '../services/users.service';
import { PwaService } from '../services/pwa.service';
import { LayoutsService } from '../services/layouts.service';
import { CurrentActiveLayout } from '../interfaces/layouts.interface';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-home',
  standalone: false,
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  topOffers: Offer[] = [];
  topNews: News[] = [];
  lastPartners: Partner[] = [];
  banners: Banner[] = [];
  currentBannerIndex = 0;
  private autoplayInterval: any;
  isOnline = true;
  isLoggedIn = false;
  categoryTitle?: string;
  groups: PartnerGroup[] = [];
  offerSeearch: string = '';
  showInstallPwa = false;
  isRunningAsApp = false;
  showInstallDialog = false;
  isRunningAsMobilePage = false;
  dontShowInstallAgain = false;
  private subscriptions: Subscription = new Subscription();
  loading = false;
  
  // Layout configuration
  currentLayout?: CurrentActiveLayout;
  showIntro = true;
  showNews = true;
  showOffers = true;
  showPartners = true;
  showBanners = true;
  customNewsTitle = 'Aktualności';
  customOffersTitle = 'Promowane oferty';
  customPartnersTitle = 'Najnowsi partnerzy';

  private handleOnline = () => this.isOnline = true;
  private handleOffline = () => this.isOnline = false;

  carouselOptions = {
    loop: true,
    autoplay: true,
    Responsive: true,
    margin: 4,
    nav: true,
    navText: ['poprzedni', 'następny'],
    autoplayTimeout: 4000,
    responsive: {
      0: {
        items: 1,
        margin: 15,
        stagePadding: 20,
        withControls: true,
        itemWidth: 340

      },
      450: {
        items: 2,
        margin: 2,

      },
      630: {
        stagePadding: 10,
        items: 3,
        margin: 2
      },
      850: {
        items: 4,
        margin: 5,
        stagePadding: 20
      },
      1100: {
        items: 5,
        margin: 5,
        stagePadding: 20
      },
      1300: {
        items: 6,
        margin: 5,
        stagePadding: 20
      }

    }
  };

  imageRoot = environment.ImagesUrl;

  constructor(
    private bannersService: BannersService,
    private usersService: UsersService,
    private offersService: OffersService,
    private router: Router,
    private pwaService: PwaService,
    private titleService: Title,
    private partnersService: PartnersService,
    private layoutService: LayoutsService
  ) { }



  ngOnInit(): void {
    this.loading = true;
    this.isLoggedIn = this.usersService.loggedIn.value;

    this.isOnline = navigator.onLine;
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    this.loadGroups();

    // Check if running as installed app
    this.isRunningAsApp = this.pwaService.isPwaInstalled();
    this.isRunningAsMobilePage = localStorage.getItem('mobileApp') === '1';
    
    this.showInstallDialog = (this.pwaService.isPwaInstalled() || !this.pwaService.isSupportedBrowser()) && this.pwaService.isMobileDevice() && localStorage.getItem('dontShowInstallPopup') !== 'true';

    // Load current layout configuration
    forkJoin({
      layout: this.layoutService.getCurrentLayout(),
      banners: this.bannersService.getActiveBanners()
    }).subscribe({
      next: ({ layout, banners }) => {
        this.currentLayout = layout;
        
        // Apply layout configuration
        this.showIntro = layout.show_intro === 1;
        this.showNews = layout.show_news === 1;
        this.showOffers = layout.show_offers === 1;
        this.showPartners = layout.show_partners === 1;
        this.showBanners = layout.show_banners === 1;
        
        // Apply custom titles if provided
        this.customNewsTitle = layout.custom_news_title || 'Aktualności';
        this.customOffersTitle = layout.custom_offers_title || 'Promowane oferty';
        this.customPartnersTitle = layout.custom_partners_title || 'Najnowsi partnerzy';
        
        // Load items from layout
        this.topNews = layout.news_items || [];
        this.topOffers = layout.offers_items || [];
        this.lastPartners = layout.partners_items || [];
        
        // Load banners
        this.banners = banners.items || [];
        
        // Start autoplay if banners exist and should be shown
        if (this.showBanners && this.banners.length > 1) {
          this.startAutoplay();
        }
        
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading layout:', err);
        this.loading = false;
      }
    });
  }

    loadGroups(): void {
    this.partnersService.getPartnerGroup().subscribe({
      next: (groups) => {
        this.groups = groups;
      },
      error: () => {
        this.groups = [];
      }
    });
  }

  onGroupSelected(groupId: number | null): void {
    // Prevent duplicate loading if the selection hasn't actually changed
    // Update category title based on selection
    if (groupId) {
      const selectedGroup = this.groups.find(group => group.id === groupId);
      // Redirect to offers page with category
      
      if (selectedGroup?.title) {
        const urlTitle = selectedGroup.title
          .toLowerCase()
          .replace(/ą/g, 'a')
          .replace(/ć/g, 'c')
          .replace(/ę/g, 'e')
          .replace(/ł/g, 'l')
          .replace(/ń/g, 'n')
          .replace(/ó/g, 'o')
          .replace(/ś/g, 's')
          .replace(/ź/g, 'z')
          .replace(/ż/g, 'z')
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '');

          this.router.navigateByUrl(`/oferty/${urlTitle}`);
        
      }
    } else {
      this.categoryTitle = undefined;
      this.updatePageTitle();
    }
  }

  onSearchOffer(): void {
    
    localStorage.setItem('offersMenuGroupId', '')
    this.offersService.searchTitle = this.offerSeearch || '';
    this.router.navigateByUrl(`/oferty`);
  }


  updatePageTitle(categoryName?: string): void {
    if (categoryName) {
      this.titleService.setTitle(`Oferty - ${categoryName} | Zgrane Grono`);
    } else {
      this.titleService.setTitle('Oferty naszych partnerów | Zgrane Grono');
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.subscriptions.unsubscribe();
    // Clean up event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    // Clean up autoplay
    this.stopAutoplay();
  }

  // Banner carousel methods
  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextBanner();
    }, 4000); // Change banner every 4 seconds
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  nextBanner() {
    if (this.banners.length > 0) {
      this.currentBannerIndex = (this.currentBannerIndex + 1) % this.banners.length;
    }
  }

  previousBanner() {
    if (this.banners.length > 0) {
      this.currentBannerIndex = this.currentBannerIndex === 0 ? this.banners.length - 1 : this.currentBannerIndex - 1;
    }
  }

  goToBanner(index: number) {
    this.currentBannerIndex = index;
    // Restart autoplay after manual navigation
    if (this.banners.length > 1) {
      this.startAutoplay();
    }
  }

  async installPwa() {
    if (this.pwaService.canInstall()) {
      const success = await this.pwaService.installPwa();
      if (!success) {
        // Show manual instructions if native prompt failed
        //alert(this.pwaService.getInstallInstructions());
      }
    } else {
      // Check if already installed
      if (window.matchMedia('(display-mode: standalone)').matches) {
        alert('Aplikacja jest już zainstalowana!');
        return;
      }
      
      // Show manual installation instructions
      alert(this.pwaService.getInstallInstructions());
    }
  }

  dismissPwa() {
    this.pwaService.dismissHomePrompt();
  }

  closeInstallDialog() {
    if (this.dontShowInstallAgain) {
      localStorage.setItem('dontShowInstallPopup', 'true');
    }
    this.showInstallDialog = false;
  }

  goToPartnerDetails(partner: Partner) {
    let urlName = partner.name
      ? partner.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'partner';
    this.router.navigate(['/partner', urlName, partner.id]);
  }

  goToOfferDetails(offer: Offer) {
    let titlePart = offer?.title
      ? offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'oferta';

    this.router.navigate(
      ['/oferta', titlePart, offer.id],
    );
  }


  goToBannerLink(banner: Banner) {
    if (banner.link) {
      // Check if it's an external link
      if (banner.link.startsWith('http://') || banner.link.startsWith('https://')) {
        try {
          const linkUrl = new URL(banner.link);
          const currentUrl = new URL(window.location.href);
          
          // If same domain, navigate internally
          if (linkUrl.hostname === currentUrl.hostname) {
            // Extract path and navigate internally
            const internalPath = linkUrl.pathname + linkUrl.search + linkUrl.hash;
            this.router.navigateByUrl(internalPath);
          } else {
            // Different domain, open in new window
            window.open(banner.link, '_blank');
          }
        } catch (error) {
          // Invalid URL, try to open in new window anyway
          window.open(banner.link, '_blank');
        }
      } else {
        // Internal route
        this.router.navigateByUrl(banner.link);
      }
    }
  }

  goToNewsDetails(news: News) {
    let titlePart = news?.title
      ? news.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      : 'wiadomosci';

    this.router.navigate(
      ['/wiadomosci', titlePart, news.id],
    );
  }
}